import { useCallback, useMemo, useState } from 'react';

import { t } from '../../../lib/i18n';
import type { DepositsOverviewRow } from '../api';
import { tierLabel } from '../labels';
import type { FinanceClient } from '../types';
import { cn } from '../utils';
import {
  DEPOSITS_ACTION_CELL,
  DEPOSITS_GRID,
  DepositRow,
  type DepositDoneMark,
} from './deposit-row';
import { PanelEmpty } from './panel-state';

interface DepositsTableProps {
  rows: DepositsOverviewRow[];
  /** The balances rollup, read only for each client's tariff caption. */
  clients: FinanceClient[];
}

const COLUMNS = ['client', 'mode', 'deposit', 'used', 'state', 'action'] as const;
const RIGHT_ALIGNED = new Set<string>(['deposit', 'action']);

/**
 * «Депозити й режими» — `Redesign-D-Admin`, «Admin · Фінанси» with
 * `finView: 'deposits'`.
 *
 * Every client with a bidding mode, a deposit or a pending refund request, in
 * the SERVER's order (refund requests first, then blocked, then over the
 * ceiling, then the rest by deposit). The table keeps what each row's action
 * did — «нагадано 11:32», «повернення ініційовано» — so a refetch after a
 * refund does not silently put the button back.
 */
export function DepositsTable({ rows, clients }: DepositsTableProps) {
  const [done, setDone] = useState<Record<string, DepositDoneMark>>({});
  const onDone = useCallback(
    (userId: string, mark: DepositDoneMark) =>
      setDone((current) => ({ ...current, [userId]: mark })),
    [],
  );

  const roles = useMemo(
    () => new Map(clients.map((client) => [client.userId, tierLabel(client.tier)])),
    [clients],
  );

  if (rows.length === 0) {
    return <PanelEmpty messageKey="adminFinance.deposits.empty" />;
  }

  return (
    <section
      role="table"
      aria-label={t('adminFinance.deposits.tableAria')}
      className="overflow-x-auto rounded-cf-lg border border-cf-line bg-cf-surface leading-[normal]"
    >
      <div
        role="row"
        className={cn(
          DEPOSITS_GRID,
          'border-b border-cf-line-soft py-2.5 font-mono text-[10px] tracking-[0.12em] text-cf-ink-subtle uppercase',
        )}
      >
        {COLUMNS.map((column) => (
          <span
            key={column}
            role="columnheader"
            className={cn(
              RIGHT_ALIGNED.has(column) && 'text-right',
              // BUG-17: the header sticks with its column — «Дія» stays labelled when scrolled.
              column === 'action' && cn(DEPOSITS_ACTION_CELL, 'flex flex-col justify-center'),
            )}
          >
            {t(`adminFinance.deposits.columns.${column}`)}
          </span>
        ))}
      </div>
      {rows.map((row) => (
        <DepositRow
          key={row.userId}
          row={row}
          role={roles.get(row.userId) ?? null}
          done={done[row.userId]}
          onDone={onDone}
        />
      ))}
    </section>
  );
}
